import React from 'react';
import { Link } from '../Typography';
import { cn } from '../../utils/cn';

export interface LogoProps {
  /** Use gradient text with highlight effect */
  withHighlight?: boolean;
  /** Size of the logo text */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Whether to wrap the logo in a link */
  withLink?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom text to display (defaults to "Orchard9") */
  text?: string;
  /** Custom href for the link (defaults to "/") */
  href?: string;
}

/**
 * Logo component for displaying the brand name with gradient styling
 * @example
 * <Logo size="lg" withHighlight />
 */
export const Logo: React.FC<LogoProps> = ({
  withHighlight = false,
  size = 'md',
  withLink = true,
  className,
  text = 'Orchard9',
  href = '/',
}) => {
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-4xl md:text-5xl',
  };

  const baseClasses = cn(
    'font-bold',
    sizeClasses[size],
    'bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent',
    withHighlight && 'via-emerald-300',
    className
  );

  const content = <span className={baseClasses}>{text}</span>;

  if (withLink) {
    return (
      <Link
        href={href}
        className="hover:opacity-80 transition-opacity inline-flex items-center gap-2"
      >
        {content}
      </Link>
    );
  }

  return content;
};
